import React, { useState } from "react";
import { DEFAULT_PAGE_SIZE, PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { DATE_DISPLAY_FORMAT, toApiDate } from "../../utils/dateUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Avatar,
  Tooltip,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Edit,
  Delete,
  Search,
  Close,
  Event,
  CalendarMonth,
  Schedule,
  LocationOn,
  People,
  Download,
  Visibility,
  EventAvailable,
  EventBusy,
  Today,
  DateRange,
  School,
  CelebrationOutlined,
  MeetingRoom,
  Assignment,
  Quiz,
} from "@mui/icons-material";

const EventsManagement = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterType, setFilterType] = useState("all");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    eventType: "",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    location: "",
    isAllDay: false,
    eventLevel: "institute",
    targetAudience: "all",
    selectedDepartments: [],
    selectedClasses: [],
    sendNotification: true,
    notifyBefore: "1day",
    isRecurring: false,
    recurringPattern: "",
    color: "#1976d2",
  });

  // Sample data for departments and classes
  const departments = [
    { id: 1, name: "Computer Science" },
    { id: 2, name: "Electronics" },
    { id: 3, name: "Mechanical" },
    { id: 4, name: "Civil" },
    { id: 5, name: "Electrical" },
  ];

  const classes = [
    { id: 1, name: "CS-A (1st Year)", department: "Computer Science" },
    { id: 2, name: "CS-B (1st Year)", department: "Computer Science" },
    { id: 3, name: "CS-A (2nd Year)", department: "Computer Science" },
    { id: 4, name: "EC-A (1st Year)", department: "Electronics" },
    { id: 5, name: "EC-B (1st Year)", department: "Electronics" },
    { id: 6, name: "ME-A (1st Year)", department: "Mechanical" },
    { id: 7, name: "CE-A (1st Year)", department: "Civil" },
  ];

  const eventTypes = [
    { value: "exam", label: "Examination", icon: <Quiz />, color: "#d32f2f" },
    {
      value: "holiday",
      label: "Holiday",
      icon: <CelebrationOutlined />,
      color: "#4caf50",
    },
    {
      value: "meeting",
      label: "Meeting",
      icon: <MeetingRoom />,
      color: "#2196f3",
    },
    {
      value: "deadline",
      label: "Deadline",
      icon: <Assignment />,
      color: "#ff9800",
    },
    {
      value: "seminar",
      label: "Seminar/Workshop",
      icon: <School />,
      color: "#9c27b0",
    },
    {
      value: "sports",
      label: "Sports Event",
      icon: <Event />,
      color: "#00bcd4",
    },
    {
      value: "cultural",
      label: "Cultural Event",
      icon: <CelebrationOutlined />,
      color: "#e91e63",
    },
    { value: "other", label: "Other", icon: <Event />, color: "#607d8b" },
  ];

  const events = [
    {
      id: 1,
      title: "Mid-term Exams",
      description: "Mid-term examinations for all departments",
      eventType: "exam",
      startDate: "2025-12-20",
      endDate: "2025-12-28",
      startTime: "09:00",
      endTime: "17:00",
      location: "Examination Halls",
      isAllDay: false,
      targetAudience: "all",
      departments: [],
      status: "upcoming",
      createdBy: "Admin",
      createdAt: "2025-12-01",
      attendees: 1500,
    },
    {
      id: 2,
      title: "Assignment Deadline",
      description: "Final submission for semester projects",
      eventType: "deadline",
      startDate: "2025-12-18",
      endDate: "2025-12-18",
      startTime: "23:59",
      endTime: "23:59",
      location: "Online Portal",
      isAllDay: false,
      targetAudience: "students",
      departments: ["Computer Science", "Electronics"],
      status: "upcoming",
      createdBy: "Admin",
      createdAt: "2025-12-05",
      attendees: 450,
    },
    {
      id: 3,
      title: "Parent-Teacher Meeting",
      description: "Annual PTM for all classes",
      eventType: "meeting",
      startDate: "2025-12-22",
      endDate: "2025-12-22",
      startTime: "10:00",
      endTime: "16:00",
      location: "Main Auditorium",
      isAllDay: false,
      targetAudience: "all",
      departments: [],
      status: "upcoming",
      createdBy: "Admin",
      createdAt: "2025-12-10",
      attendees: 2000,
    },
    {
      id: 4,
      title: "Winter Break",
      description: "Institute closed for winter holidays",
      eventType: "holiday",
      startDate: "2025-12-25",
      endDate: "2026-01-01",
      startTime: "",
      endTime: "",
      location: "",
      isAllDay: true,
      targetAudience: "all",
      departments: [],
      status: "upcoming",
      createdBy: "Admin",
      createdAt: "2025-12-01",
      attendees: 0,
    },
    {
      id: 5,
      title: "Annual Sports Day",
      description: "Inter-department sports competition",
      eventType: "sports",
      startDate: "2026-01-15",
      endDate: "2026-01-16",
      startTime: "08:00",
      endTime: "18:00",
      location: "Sports Ground",
      isAllDay: false,
      targetAudience: "all",
      departments: [],
      status: "upcoming",
      createdBy: "Admin",
      createdAt: "2025-12-15",
      attendees: 1800,
    },
    {
      id: 6,
      title: "Orientation Day",
      description: "Orientation for new batch students",
      eventType: "seminar",
      startDate: "2025-12-10",
      endDate: "2025-12-10",
      startTime: "09:00",
      endTime: "13:00",
      location: "Main Auditorium",
      isAllDay: false,
      targetAudience: "students",
      departments: [],
      status: "completed",
      createdBy: "Admin",
      createdAt: "2025-12-01",
      attendees: 500,
    },
  ];

  const getEventTypeInfo = (type) => {
    return eventTypes.find((et) => et.value === type) || eventTypes[7];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "upcoming":
        return "primary";
      case "ongoing":
        return "success";
      case "completed":
        return "default";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const columns = [
    {
      field: "title",
      headerName: "Event",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const typeInfo = getEventTypeInfo(params.row.eventType);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: typeInfo.color,
                width: 36,
                height: 36,
              }}
            >
              {typeInfo.icon}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {params.row.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {typeInfo.label}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "startDate",
      headerName: "Date",
      width: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {new Date(params.row.startDate).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </Typography>
          {params.row.startDate !== params.row.endDate && (
            <Typography variant="caption" color="text.secondary">
              to{" "}
              {new Date(params.row.endDate).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
              })}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "time",
      headerName: "Time",
      width: 120,
      renderCell: (params) =>
        params.row.isAllDay ? (
          <Chip label="All Day" size="small" variant="outlined" />
        ) : (
          <Typography variant="body2">
            {params.row.startTime} - {params.row.endTime}
          </Typography>
        ),
    },
    {
      field: "location",
      headerName: "Location",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <LocationOn fontSize="small" color="action" />
          <Typography variant="body2">
            {params.row.location || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "targetAudience",
      headerName: "Audience",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={
            params.row.targetAudience === "all"
              ? "Everyone"
              : params.row.targetAudience
          }
          size="small"
          variant="outlined"
          sx={{ textTransform: "capitalize" }}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          size="small"
          color={getStatusColor(params.row.status)}
          sx={{ textTransform: "capitalize" }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => handleViewEvent(params.row)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditEvent(params.row)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error">
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const handleViewEvent = (event) => {
    setSelectedEvent(event);
    setViewDialogOpen(true);
  };

  const handleEditEvent = (event) => {
    setFormData({
      title: event.title,
      description: event.description,
      eventType: event.eventType,
      startDate: event.startDate,
      endDate: event.endDate,
      startTime: event.startTime,
      endTime: event.endTime,
      location: event.location,
      isAllDay: event.isAllDay,
      eventLevel: event.eventLevel || "institute",
      targetAudience: event.targetAudience,
      selectedDepartments: event.departments || [],
      selectedClasses: event.classes || [],
      sendNotification: true,
      notifyBefore: "1day",
      isRecurring: false,
      recurringPattern: "",
      color: getEventTypeInfo(event.eventType).color,
    });
    setSelectedEvent(event);
    setCreateDialogOpen(true);
  };

  const handleCreateNew = () => {
    setFormData({
      title: "",
      description: "",
      eventType: "",
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
      location: "",
      isAllDay: false,
      eventLevel: "institute",
      targetAudience: "all",
      selectedDepartments: [],
      selectedClasses: [],
      sendNotification: true,
      notifyBefore: "1day",
      isRecurring: false,
      recurringPattern: "",
      color: "#1976d2",
    });
    setSelectedEvent(null);
    setCreateDialogOpen(true);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "eventType") {
      const typeInfo = getEventTypeInfo(value);
      setFormData((prev) => ({ ...prev, color: typeInfo.color }));
    }
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch =
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === "all" || event.eventType === filterType;
    const matchesTab =
      activeTab === 0 ||
      (activeTab === 1 && event.status === "upcoming") ||
      (activeTab === 2 && event.status === "ongoing") ||
      (activeTab === 3 && event.status === "completed");
    return matchesSearch && matchesType && matchesTab;
  });

  const stats = {
    total: events.length,
    upcoming: events.filter((e) => e.status === "upcoming").length,
    ongoing: events.filter((e) => e.status === "ongoing").length,
    completed: events.filter((e) => e.status === "completed").length,
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Events Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage institute events, holidays, and important dates
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" startIcon={<Download />} disabled={filteredEvents.length === 0}>
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateNew}
          >
            Create Event
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <CalendarMonth
                sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
              />
              <Typography variant="h4" fontWeight="bold">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Events
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <EventAvailable
                sx={{ fontSize: 40, color: "info.main", mb: 1 }}
              />
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {stats.upcoming}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upcoming
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <Today sx={{ fontSize: 40, color: "success.main", mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {stats.ongoing}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ongoing
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 2 }}>
              <EventBusy
                sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
              />
              <Typography variant="h4" fontWeight="bold" color="text.secondary">
                {stats.completed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs and Filters */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ px: 2 }}
          >
            <Tab label="All Events" />
            <Tab label="Upcoming" />
            <Tab label="Ongoing" />
            <Tab label="Completed" />
          </Tabs>
        </Box>

        <Box
          sx={{
            p: 2,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            placeholder="Search events..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Event Type</InputLabel>
            <Select
              value={filterType}
              label="Event Type"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              {eventTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Events Table */}
        <DataGrid
          rows={filteredEvents}
          columns={columns}
          pageSize={DEFAULT_PAGE_SIZE}
          rowsPerPageOptions={PAGINATION_PAGE_SIZE_OPTIONS}
          autoHeight
          disableSelectionOnClick
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": { py: 1 },
            "& .MuiDataGrid-row:hover": { bgcolor: "action.hover" },
          }}
        />
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setCreateDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              {selectedEvent ? "Edit Event" : "Create New Event"}
            </Typography>
            <IconButton onClick={() => setCreateDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Event Title"
                fullWidth
                required
                value={formData.title}
                onChange={(e) => handleFormChange("title", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) =>
                  handleFormChange("description", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={formData.eventType}
                  label="Event Type"
                  onChange={(e) =>
                    handleFormChange("eventType", e.target.value)
                  }
                >
                  {eventTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Avatar
                          sx={{ bgcolor: type.color, width: 24, height: 24 }}
                        >
                          {React.cloneElement(type.icon, {
                            sx: { fontSize: 14 },
                          })}
                        </Avatar>
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Location"
                fullWidth
                value={formData.location}
                onChange={(e) => handleFormChange("location", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isAllDay}
                    onChange={(e) =>
                      handleFormChange("isAllDay", e.target.checked)
                    }
                  />
                }
                label="All Day Event"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Start Date"
                format={DATE_DISPLAY_FORMAT}
                value={formData.startDate ? new Date(formData.startDate) : null}
                onChange={(val) => handleFormChange("startDate", toApiDate(val))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="End Date"
                format={DATE_DISPLAY_FORMAT}
                value={formData.endDate ? new Date(formData.endDate) : null}
                onChange={(val) => handleFormChange("endDate", toApiDate(val))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                  },
                }}
              />
            </Grid>

            {!formData.isAllDay && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Start Time"
                    type="time"
                    fullWidth
                    value={formData.startTime}
                    onChange={(e) =>
                      handleFormChange("startTime", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="End Time"
                    type="time"
                    fullWidth
                    value={formData.endTime}
                    onChange={(e) =>
                      handleFormChange("endTime", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Target Audience
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Event Level</InputLabel>
                <Select
                  value={formData.eventLevel}
                  label="Event Level"
                  onChange={(e) =>
                    handleFormChange("eventLevel", e.target.value)
                  }
                >
                  <MenuItem value="institute">
                    Institute Level (Everyone)
                  </MenuItem>
                  <MenuItem value="department">Department Level</MenuItem>
                  <MenuItem value="class">Class Level</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Audience Type</InputLabel>
                <Select
                  value={formData.targetAudience}
                  label="Audience Type"
                  onChange={(e) =>
                    handleFormChange("targetAudience", e.target.value)
                  }
                >
                  <MenuItem value="all">All (Students + Staff)</MenuItem>
                  <MenuItem value="students">Students Only</MenuItem>
                  <MenuItem value="teachers">Teachers Only</MenuItem>
                  <MenuItem value="staff">All Staff</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.eventLevel === "department" && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Departments</InputLabel>
                  <Select
                    multiple
                    value={formData.selectedDepartments}
                    label="Select Departments"
                    onChange={(e) =>
                      handleFormChange("selectedDepartments", e.target.value)
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.name}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {formData.eventLevel === "class" && (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Filter by Department</InputLabel>
                    <Select
                      multiple
                      value={formData.selectedDepartments}
                      label="Filter by Department"
                      onChange={(e) =>
                        handleFormChange("selectedDepartments", e.target.value)
                      }
                      renderValue={(selected) => selected.join(", ")}
                    >
                      {departments.map((dept) => (
                        <MenuItem key={dept.id} value={dept.name}>
                          {dept.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Select Classes</InputLabel>
                    <Select
                      multiple
                      value={formData.selectedClasses}
                      label="Select Classes"
                      onChange={(e) =>
                        handleFormChange("selectedClasses", e.target.value)
                      }
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {classes
                        .filter(
                          (cls) =>
                            formData.selectedDepartments.length === 0 ||
                            formData.selectedDepartments.includes(
                              cls.department
                            )
                        )
                        .map((cls) => (
                          <MenuItem key={cls.id} value={cls.name}>
                            {cls.name} ({cls.department})
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Notification Settings
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.sendNotification}
                    onChange={(e) =>
                      handleFormChange("sendNotification", e.target.checked)
                    }
                  />
                }
                label="Send Notification"
              />
            </Grid>
            {formData.sendNotification && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Notify Before</InputLabel>
                  <Select
                    value={formData.notifyBefore}
                    label="Notify Before"
                    onChange={(e) =>
                      handleFormChange("notifyBefore", e.target.value)
                    }
                  >
                    <MenuItem value="1hour">1 Hour Before</MenuItem>
                    <MenuItem value="1day">1 Day Before</MenuItem>
                    <MenuItem value="3days">3 Days Before</MenuItem>
                    <MenuItem value="1week">1 Week Before</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={selectedEvent ? <Edit /> : <Add />}
          >
            {selectedEvent ? "Update Event" : "Create Event"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Event Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setViewDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        {selectedEvent && (
          <>
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: getEventTypeInfo(selectedEvent.eventType).color,
                    }}
                  >
                    {getEventTypeInfo(selectedEvent.eventType).icon}
                  </Avatar>
                  <Typography variant="h6">{selectedEvent.title}</Typography>
                </Box>
                <IconButton onClick={() => setViewDialogOpen(false)}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Event />
                  </ListItemIcon>
                  <ListItemText
                    primary="Event Type"
                    secondary={getEventTypeInfo(selectedEvent.eventType).label}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <DateRange />
                  </ListItemIcon>
                  <ListItemText
                    primary="Date"
                    secondary={
                      selectedEvent.startDate === selectedEvent.endDate
                        ? new Date(selectedEvent.startDate).toLocaleDateString(
                            "en-IN",
                            {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }
                          )
                        : `${new Date(
                            selectedEvent.startDate
                          ).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                          })} - ${new Date(
                            selectedEvent.endDate
                          ).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}`
                    }
                  />
                </ListItem>
                {!selectedEvent.isAllDay && (
                  <ListItem>
                    <ListItemIcon>
                      <Schedule />
                    </ListItemIcon>
                    <ListItemText
                      primary="Time"
                      secondary={`${selectedEvent.startTime} - ${selectedEvent.endTime}`}
                    />
                  </ListItem>
                )}
                {selectedEvent.location && (
                  <ListItem>
                    <ListItemIcon>
                      <LocationOn />
                    </ListItemIcon>
                    <ListItemText
                      primary="Location"
                      secondary={selectedEvent.location}
                    />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemIcon>
                    <People />
                  </ListItemIcon>
                  <ListItemText
                    primary="Target Audience"
                    secondary={
                      selectedEvent.targetAudience === "all"
                        ? "Everyone"
                        : selectedEvent.targetAudience
                    }
                  />
                </ListItem>
                {selectedEvent.description && (
                  <ListItem>
                    <ListItemText
                      primary="Description"
                      secondary={selectedEvent.description}
                    />
                  </ListItem>
                )}
              </List>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => {
                  setViewDialogOpen(false);
                  handleEditEvent(selectedEvent);
                }}
              >
                Edit
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default EventsManagement;
